import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Search as SearchIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import baseurl from '../Baseurl/baseurl';
import * as XLSX from 'xlsx';
import Person from '@mui/icons-material/Person';
import QrCode2 from '@mui/icons-material/QrCode2';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import Badge from '@mui/icons-material/Badge';
import Phone from '@mui/icons-material/Phone';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import {
  Eyebrow,
  Button as UIButton,
  Card as GlassCard,
  Input as UIInput,
  StatusBadge,
  Avatar as UIAvatar,
  Loader,
  EmptyState,
} from '../ui';

const ViewModal = ({ open, onClose, member }) => {
  if (!member) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { backgroundImage: 'none', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 3,
          py: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ color: 'text.primary' }}>
          🧾 Referral Details
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mt: 1 }}>
          {/* Member Overview */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'center', sm: 'flex-start' },
              gap: 3,
              p: 2,
              mb: 4,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            <UIAvatar
              src={member.profile_image ? `${baseurl}/${member.profile_image.replace(/\\/g, '/')}` : undefined}
              name={`${member.first_name || ''} ${member.last_name || ''}`}
              size="xl"
            />

            <Box textAlign={{ xs: 'center', sm: 'left' }}>
              <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary' }}>
                {member.first_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {member.email}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: { xs: 'center', sm: 'flex-start' }, flexWrap: 'wrap' }}>
                <StatusBadge status={member.status} />
              </Box>
            </Box>
          </Box>

          {/* Referral Info */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{ color: 'text.primary' }}>
                  📢 Referral Info
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><Person sx={{ color: 'primary.main' }} /></ListItemIcon>
                    <ListItemText primary="Referrer Name" secondary={member.Referral?.referral_name || 'N/A'} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><QrCode2 sx={{ color: 'primary.main' }} /></ListItemIcon>
                    <ListItemText primary="Referral Code" secondary={member.Referral?.referral_code || 'N/A'} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CalendarMonth sx={{ color: 'primary.main' }} /></ListItemIcon>
                    <ListItemText primary="Join Date" secondary={formatDate(member.join_date)} />
                  </ListItem>
                </List>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{ color: 'text.primary' }}>
                  📄 Additional Info
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>  <DisplaySettingsIcon sx={{ color: 'primary.main' }} /></ListItemIcon>
                    <ListItemText primary="Application ID" secondary={member.application_id || 'N/A'} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Phone sx={{ color: 'primary.main' }} /></ListItemIcon>
                    <ListItemText primary="Contact Number" secondary={member.contact_no || 'N/A'} />
                  </ListItem>
                </List>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <UIButton variant="primary" onClick={onClose}>Close</UIButton>
      </DialogActions>
    </Dialog>
  );
};

const EditModal = ({ open, onClose, member, onSubmit }) => {
  const [status, setStatus] = useState(member?.status || 'Pending');

  useEffect(() => {
    if (member) {
      setStatus(member.status || 'Pending');
    }
  }, [member]);

  if (!member) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundImage: 'none', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ color: 'text.primary' }}>Edit Referral</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Member Name"
                  value={`${member.first_name || ''} ${member.last_name || ''}`}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={member.email || ''}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Referrer Name"
                  value={member.Referral?.referral_name || ''}
                  variant="outlined"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Referral Code"
                  value={member.Referral?.referral_code || ''}
                  variant="outlined"
                />
              </Grid>
              <Grid size={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="Approved">Approved</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <UIButton variant="ghost" type="button" onClick={onClose}>Cancel</UIButton>
          <UIButton type="submit" variant="primary">
            Save Changes
          </UIButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const ReferralSystemComponent = () => {
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [referralData, setReferralData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const adminRole = typeof window !== 'undefined' ? localStorage.getItem('adminRole') : null;

  // Fetch referral data from API
  useEffect(() => {
    const fetchReferralData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${baseurl}/api/member/all`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.msg || 'Failed to fetch referral data');
        }

        // Filter members that have referral information
        const membersWithReferral = data.data.filter(member =>
          member.Referral && Object.keys(member.Referral).length > 0
        );

        setReferralData(membersWithReferral);
      } catch (err) {
        setError(err.message || 'An error occurred while fetching referral data');
        console.error('Error fetching referral data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReferralData();
  }, []);

  // Handle export to Excel
  const handleExport = () => {
    // Prepare data for export
    const exportData = filteredReferralData.map(member => ({ "Member Name": `${member.first_name} ${member.last_name}`, "Email": member.email, "Referrer": member.Referral?.referral_name || 'N/A', "Referral Code": member.Referral?.referral_code || 'N/A', "Join Date": formatDate(member.join_date), "Status": member.status || 'Pending', "Application ID": member.application_id || 'N/A', "Contact": member.contact_no || 'N/A'
    }));

    // Create worksheet and workbook
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Referral Members");

    // Generate Excel file and download
    XLSX.writeFile(workbook, "referral_members.xlsx");
  };

  const handleViewModalOpen = (member) => {
    setSelectedMember(member);
    setViewModalOpen(true);
  };

  const handleViewModalClose = () => {
    setViewModalOpen(false);
    setSelectedMember(null);
  };

  const handleEditModalOpen = (member) => {
    setSelectedMember(member);
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setSelectedMember(null);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    // Add your edit submission logic here
    handleEditModalClose();
  };

  const handleDelete = async (member) => {
    if (window.confirm(`Are you sure you want to delete ${member.first_name} ${member.last_name}?`)) {
      try {
        const response = await fetch(`${baseurl}/api/member/delete/${member.mid}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.msg || 'Failed to delete member');
        }

        // Remove the deleted member from the state
        setReferralData(referralData.filter(m => m.mid !== member.mid));

        alert('Member deleted successfully');
      } catch (err) {
        alert(err.message);
        console.error('Error deleting member:', err);
      }
    }
  };

  const getStatusChip = (status) => {
    const displayStatus = status || 'Pending';
    return <StatusBadge status={displayStatus} />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter, sort and paginate referral data
  const filteredReferralData = referralData.filter(member => {
    if (!member) return false;
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      (member.first_name?.toLowerCase().includes(searchLower)) ||
      (member.last_name?.toLowerCase().includes(searchLower)) ||
      (member.email?.toLowerCase().includes(searchLower)) ||
      (member.Referral?.referral_name?.toLowerCase().includes(searchLower)) ||
      (member.Referral?.referral_code?.toLowerCase().includes(searchLower))
    );

    const matchesStatus = statusFilter === 'All' || member.status === statusFilter;

    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'name':
        aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
        bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
        break;
      case 'email':
        aValue = a.email?.toLowerCase() || '';
        bValue = b.email?.toLowerCase() || '';
        break;
      case 'referrer':
        aValue = a.Referral?.referral_name?.toLowerCase() || '';
        bValue = b.Referral?.referral_name?.toLowerCase() || '';
        break;
      case 'code':
        aValue = a.Referral?.referral_code?.toLowerCase() || '';
        bValue = b.Referral?.referral_code?.toLowerCase() || '';
        break;

      case 'status':
        aValue = a.status || '';
        bValue = b.status || '';
        break;
      default:
        aValue = a.first_name?.toLowerCase() || '';
        bValue = b.first_name?.toLowerCase() || '';
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Pagination calculations
  const pageCount = Math.ceil(filteredReferralData.length / itemsPerPage);
  const paginatedData = filteredReferralData.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Reset to page 1 when filters/search change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, sortBy, sortOrder]);

  if (loading) {
    return <Loader label="Loading referral data…" />;
  }

  if (error) {
    return (
      <div className="py-10">
        <EmptyState
          title="Error Loading Referral Data"
          description={error}
          action={
            <UIButton variant="primary" onClick={() => window.location.reload()}>
              Retry
            </UIButton>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page head */}
      <div className="mb-5 flex flex-col gap-3 border-b border-line/10 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Eyebrow>Referrals</Eyebrow>
          <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink">
            Referral <em className="kbc-serif">System</em>
          </h1>
          <p className="mt-1 max-w-prose text-sm leading-relaxed text-text">
            Manage referral members and track referral performance.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <UIButton variant="ghost" leftIcon={<FileDownloadIcon fontSize="small" />} onClick={handleExport}>
            Export
          </UIButton>
        </div>
      </div>

      {/* Referral Members Section */}
      <GlassCard padding="none">
          {/* Search Bar */}
          <Box
            sx={{
              p: 3,
              display: 'flex',
              gap: 2,
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' }
            }}
          >
            <div className="w-full md:w-auto md:min-w-[300px]">
              <UIInput
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<SearchIcon fontSize="small" />}
              />
            </div>
            <UIButton
              variant="ghost"
              leftIcon={<FilterListIcon fontSize="small" />}
              onClick={() => setFilterDialogOpen(true)}
            >
              Filter
            </UIButton>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortBy}
                label="Sort by"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="name">Member Name</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="referrer">Referrer</MenuItem>
                <MenuItem value="code">Referral Code</MenuItem>
                <MenuItem value="status">Status</MenuItem>
              </Select>
            </FormControl>
            <UIButton
              variant="ghost"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </UIButton>
          </Box>

          {/* Table */}
          <TableContainer sx={{ backgroundColor: 'transparent' }}>
            <Table sx={{ '& .MuiTableCell-root': { borderColor: 'divider' } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Member</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Referrer</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Referral Code</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Join Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 0, border: 0 }}>
                      <EmptyState
                        title={searchTerm ? 'No matching referrals found' : 'No referral data available'}
                        description={searchTerm ? 'Try a different search term.' : 'Referral members will appear here once available.'}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((member) => (
                    <TableRow
                      key={member.mid}
                      sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <UIAvatar
                            name={`${member.first_name || ''} ${member.last_name || ''}`}
                            size="sm"
                          />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                              {member.first_name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {member.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                          {member.Referral?.referral_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                          {member.Referral?.referral_code || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {formatDate(member.join_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(member.status)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewModalOpen(member)}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                          >
                            <InfoIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(member)}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {filteredReferralData.length === 0
                ? 'Showing 0 entries'
                : `Showing ${(page - 1) * itemsPerPage + 1}-${Math.min(page * itemsPerPage, filteredReferralData.length)} of ${filteredReferralData.length} entries (${itemsPerPage} per page)`}
            </Typography>
            <Pagination
              count={pageCount}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
            />
          </Box>
      </GlassCard>

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { backgroundImage: 'none', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' } }}
      >
        <DialogTitle sx={{ color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>Filter Referrals</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <UIButton variant="ghost" onClick={() => setFilterDialogOpen(false)}>Cancel</UIButton>
          <UIButton variant="primary" onClick={() => setFilterDialogOpen(false)}>Apply</UIButton>
        </DialogActions>
      </Dialog>

      <ViewModal
        open={viewModalOpen}
        onClose={handleViewModalClose}
        member={selectedMember}
      />
      <EditModal
        open={editModalOpen}
        onClose={handleEditModalClose}
        member={selectedMember}
        onSubmit={handleEditSubmit}
      />
    </div>
  );
};

export default ReferralSystemComponent;
